import { DynamicSettings } from "Components/admin/DynamicSettings";
import {
	ARGENTINIAN_CULTURE_CODE,
	BRASILIAN_CULTURE_CODE,
	BRITISH_CULTURE_CODE,
	CHILEAN_CULTURE_CODE,
	COLOMBIAN_CULTURE_CODE,
	DOMINICA_CULTURE_CODE,
	ECUADORIAN_CULTURE_CODE,
	PARAGUAYAN_CULTURE_CODE,
	PERUVIAN_CULTURE_CODE,
	PROMO_CODE_URL_KEYS,
	SPANISH_CULTURE_CODE,
	URUGUAYAN_CULTURE_CODE,
	USA_CULTURE_CODE,
	VENEZUELAN_CULTURE_CODE,
} from "Services/constants";
import { MappedUserInfo } from "Shared/models/MappedUserInfo";

export interface GetPromoCodesParams {
	userInfo: MappedUserInfo;
	culture: string;
	dynamicSettings: DynamicSettings;
}

export const decode = (input: string) => input.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec));

const getPromoCodesFromUrl = () => {
	const urlElems = window.location.href.split("?");

	if (urlElems.length < 2) return undefined;

	const queryElems = urlElems[1].split("&");

	return queryElems
		.map((elem) => elem.split("=") as [string, string])
		.filter(([key]) => PROMO_CODE_URL_KEYS.includes(key.toLowerCase()))
		.map(([, value]) => value);
};

const getDynamicPromoCode = (params: GetPromoCodesParams) => {
	const { userInfo, culture, dynamicSettings } = params;
	// DEVNOTE: The sorting is because BancoEstado (BEC-...) is stronger than DC (DCB-...)
	// FIXME Is it needed still?
	const sortedSettings = dynamicSettings.DynamicPromoCodeSettings.sort((a, b) =>
		a.ProgramCodeAndLevel > b.ProgramCodeAndLevel ? 1 : -1
	);

	if (userInfo?.ProgramCodesWithLevels && userInfo.ProgramCodesWithLevels.length > 0) {
		const promoCode = sortedSettings.find(
			(item) =>
				item.ProgramCodeAndLevel &&
				(!item.Culture || item.Culture.toLowerCase() === culture.toLowerCase()) &&
				(!item.RoleCode || item.RoleCode.toLowerCase() === userInfo?.RoleCode?.toLowerCase()) &&
				userInfo.ProgramCodesWithLevels.some(
					(pcwl) => item.ProgramCodeAndLevel === `${pcwl.ProgramCode}-${pcwl.ProgramLevel}`
				)
		)?.Code;

		if (promoCode) return promoCode;
	}

	const promoCode = sortedSettings.find(
		(item) =>
			!item.ProgramCodeAndLevel &&
			(!item.Culture || item.Culture.toLowerCase() === culture.toLowerCase()) &&
			(!item.RoleCode || item.RoleCode.toLowerCase() === userInfo?.RoleCode?.toLowerCase())
	)?.Code;

	if (promoCode) return promoCode;

	return "";
};

const getDefaultPromoCode = (culture: string, dynamicSettings: DynamicSettings) => {
	switch (culture.toLowerCase()) {
		case CHILEAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeCl;
		case PERUVIAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodePe;
		case ARGENTINIAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeAr;
		case COLOMBIAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeCo;
		case USA_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeUs;
		case BRASILIAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeBr;
		case PARAGUAYAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodePy;
		case URUGUAYAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeUy;
		case ECUADORIAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeEc;
		case DOMINICA_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeDo;
		case BRITISH_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeGb;
		case VENEZUELAN_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeVe;
		case SPANISH_CULTURE_CODE:
			return dynamicSettings.DefaultPromotionCodeEs;
		default:
			return "";
	}
};

const getDefaultSuperPromoCode = (culture: string, dynamicSettings: DynamicSettings) =>
	dynamicSettings.DefaultSuperPromoCodeSettings?.find(
		(setting) => setting.Culture.toLowerCase() === culture.toLowerCase()
	)?.Code || "";

export const getPromoCodes = (params: GetPromoCodesParams) => {
	const { culture, dynamicSettings } = params;
	const promoCodesFromUrl = getPromoCodesFromUrl();
	if (promoCodesFromUrl && promoCodesFromUrl.length > 0) return promoCodesFromUrl;

	if (!dynamicSettings) return [];

	// DEVNOTE: Navitaire dynamic promo code
	const dynamicPromoCode = getDynamicPromoCode(params);

	// DEVNOTE: Navitaire default promo code
	const defaultPromoCode = getDefaultPromoCode(culture, dynamicSettings);

	// DEVNOTE: Default super promo code
	const defaultSuperPromoCode = getDefaultSuperPromoCode(culture, dynamicSettings);

	const promoCodes = [dynamicPromoCode || defaultPromoCode || "", defaultSuperPromoCode].filter((code) => code);
	return promoCodes;
};
